import React, {useEffect, useState} from "react";

/**
 * CopyButton — кнопка «Копировать» рядом с реквизитом или суммой.
 *
 * Мерчант переносит номер и сумму в банковское приложение на том же телефоне;
 * набирать их руками — главный источник ошибок перевода. Если буфер обмена
 * недоступен (старый браузер, запрет страницы), подпись говорит об этом, а не
 * делает вид, что значение скопировано. disabled — реквизиты могли устареть
 * или блок занят запросом: копировать в этот момент нельзя, как и нажимать
 * соседние кнопки.
 */
export function CopyButton({value, label, disabled = false}: {value: string; label: string; disabled?: boolean}): JSX.Element {
    const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
    useEffect(() => {
        if (state === "idle") return;
        const timer = window.setTimeout(() => setState("idle"), 2_000);
        return () => window.clearTimeout(timer);
    }, [state]);
    const copy = async (): Promise<void> => {
        setState(await copyToClipboard(value) ? "copied" : "failed");
    };
    return <button type="button" className="btn btn-ghost btn-sm copy-button" aria-label={`Копировать ${label}`} disabled={disabled} onClick={() => void copy()}>
        {state === "copied" ? "Скопировано" : state === "failed" ? "Не скопировано" : "Копировать"}
    </button>;
}

/** Копирует текст в буфер обмена; false — браузер не дал, и об этом нужно сказать человеку. */
export async function copyToClipboard(value: string): Promise<boolean> {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(value);
            return true;
        }
    } catch {
        // Запрет страницы или нет фокуса — пробуем выделением.
    }
    return copyWithSelection(value);
}

function copyWithSelection(value: string): boolean {
    const field = document.createElement("textarea");
    field.value = value;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    try { return document.execCommand("copy"); }
    catch { return false; }
    finally { field.remove(); }
}
